import axios from "axios";
import { DataTable } from "simple-datatables";
import VMasker from "vanilla-masker";
import { showAlert, handleRequestError, showConfirm } from "../../utils/alert";

export default () => ({
  hasAccessModule: true,
  isLoading: true,
  subscriptionData: null,
  invoicesList: [],
  invoicesDataTable: null,
  isPaymentModalOpen: false,
  isSubmittingPayment: false,
  modalMode: "update",
  isCancelModalOpen: false,
  isCanceling: false,
  cancelReason: "",

  paymentForm: {
    billingType: "PIX",
    creditCard: { holderName: "", number: "", expiry: "", ccv: "" },
  },

  async initSubscription() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    try {
      this.isLoading = true;
      // Fazemos a chamada diretamente. Se faltar permissão, a API retorna 403.
      await Promise.all([
        this.fetchSubscriptionDetails(),
        this.fetchInvoicesData(),
      ]);

      this.isLoading = false;

      setTimeout(() => {
        try {
          this.renderInvoicesTable();
        } catch (error) {
          console.error("Erro ao renderizar a tabela:", error);
        }
      }, 100);
    } catch (error) {
      // Captura a falha de permissão
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        this.isLoading = false;
        return;
      }
      if (error.response && [401, 402].includes(error.response.status)) return;

      this.isLoading = false;
      if (error.response && error.response.status === 429) {
        console.warn("Rate limit API threshold reached.");
      } else {
        console.error("Infrastructure or network error:", error);
      }
    }
  },

  async fetchSubscriptionDetails() {
    const response = await axios.get("/api/client/subscription/current");
    this.subscriptionData = response.data;
  },

  async fetchInvoicesData() {
    const response = await axios.get("/api/client/subscription/invoices");
    this.invoicesList = response.data.data || [];
  },

  // ================= UTILS E STATUS =================

  getStatusBadge() {
    if (!this.subscriptionData)
      return {
        text: "Desconhecido",
        class: "bg-gray-100 text-gray-800 ring-gray-500/10",
      };
    const status = this.subscriptionData.global_status;
    switch (status) {
      case "ACTIVE":
        return {
          text: "Ativa",
          class: "bg-green-50 text-green-700 ring-green-600/20",
        };
      case "PENDING":
        return {
          text: "Aguardando Pagamento",
          class: "bg-yellow-50 text-yellow-800 ring-yellow-600/20",
        };
      case "OVERDUE":
        return {
          text: "Vencida",
          class: "bg-red-50 text-red-700 ring-red-600/10",
        };
      case "CANCELLED":
        return {
          text: "Cancelada",
          class: "bg-gray-50 text-gray-600 ring-gray-500/10",
        };
      case "TRIAL":
        return {
          text: "Período de Teste",
          class: "bg-blue-50 text-blue-700 ring-blue-600/20",
        };
      case "SUSPENDED":
        return {
          text: "Suspenso",
          class: "bg-red-50 text-red-700 ring-red-600/10",
        };
      default:
        return {
          text: status,
          class: "bg-gray-100 text-gray-800 ring-gray-500/10",
        };
    }
  },

  formatPrice(price) {
    return parseFloat(price).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  },

  getInvoiceStatusBadge(status) {
    switch (status) {
      case "RECEIVED":
      case "CONFIRMED":
        return `<span class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-bold text-green-700 ring-1 ring-inset ring-green-600/20">Pago</span>`;
      case "PENDING":
        return `<span class="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-bold text-yellow-800 ring-1 ring-inset ring-yellow-600/20">Aguardando</span>`;
      case "OVERDUE":
        return `<span class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-bold text-red-700 ring-1 ring-inset ring-red-600/10">Vencido</span>`;
      case "CANCELLED":
        return `<span class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-bold text-gray-600 ring-1 ring-inset ring-gray-500/10">Cancelado</span>`;
      default:
        return `<span class="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-bold text-gray-600 ring-1 ring-inset ring-gray-500/10">${status}</span>`;
    }
  },

  getBillingTypeLabel(type) {
    const types = {
      PIX: "PIX",
      BOLETO: "Boleto",
      CREDIT_CARD: "Cartão de Crédito",
    };
    return types[type] || type;
  },

  getCycleLabel(cycle) {
    const cycles = {
      MONTHLY: "Mensal",
      SEMIANNUALLY: "Semestral",
      YEARLY: "Anual",
    };
    return cycles[cycle] || "";
  },

  formatDate(dateString) {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  },

  openSecureInvoice() {
    if (this.subscriptionData?.subscription_details?.invoice_url) {
      window.open(
        this.subscriptionData.subscription_details.invoice_url,
        "_blank",
      );
    } else {
      showAlert("O link da fatura não está disponível no momento.", "warning");
    }
  },

  // ================= DATA TABLE RENDER =================

  renderInvoicesTable() {
    const tbody = document.querySelector("#invoicesTable tbody");
    if (!tbody) {
      console.warn("Elemento tbody não encontrado.");
      return;
    }

    if (this.invoicesDataTable) {
      this.invoicesDataTable.destroy();
    }

    tbody.innerHTML = this.invoicesList
      .map((invoice) => {
        let actionButtons = "";
        if (invoice.status === "PENDING" || invoice.status === "OVERDUE") {
          actionButtons = `
            <a href="${invoice.invoice_url}" target="_blank" class="inline-flex items-center justify-center rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-dark">
              Pagar Agora
            </a>
          `;
        } else if (
          invoice.status === "RECEIVED" ||
          invoice.status === "CONFIRMED"
        ) {
          const receiptLink = invoice.transaction_receipt_url
            ? `<a href="${invoice.transaction_receipt_url}" target="_blank" class="text-xs text-blue-600 hover:underline">Ver Recibo</a>`
            : "";
          actionButtons = receiptLink;
        }

        return `
          <tr>
            <td class="py-4 px-4 xl:px-6 font-medium text-slate-800">${this.formatDate(invoice.due_date)}</td>
            <td class="py-4 px-4 xl:px-6 text-slate-600">${this.getBillingTypeLabel(invoice.billing_type)}</td>
            <td class="py-4 px-4 xl:px-6 font-semibold text-slate-700">${this.formatPrice(invoice.value)}</td>
            <td class="py-4 px-4 xl:px-6">${this.getInvoiceStatusBadge(invoice.status)}</td>
            <td class="text-left py-4 px-4 xl:px-6">
              <div class="flex justify-start items-center gap-2">
                ${actionButtons}
              </div>
            </td>
          </tr>
        `;
      })
      .join("");

    this.invoicesDataTable = new DataTable("#invoicesTable", {
      searchable: false,
      perPageSelect: false,
      sortable: false,
      labels: {
        info: "Mostrando de {start} até {end} de {rows} faturas",
        noRows: "Nenhum histórico de pagamento encontrado.",
      },
    });
  },

  openChangePaymentModal() {
    this.modalMode = "update"; // Define modo de atualização
    this.resetPaymentForm();
    this.isPaymentModalOpen = true;
  },

  openReactivateModal() {
    this.modalMode = "reactivate"; // Define modo de reativação
    this.resetPaymentForm();
    this.isPaymentModalOpen = true;
  },

  resetPaymentForm() {
    const currentBillingType =
      this.subscriptionData?.subscription_details?.billing_type || "PIX";

    this.paymentForm = {
      billingType: currentBillingType,
      creditCard: {
        holderName: "",
        number: "",
        expiry: "",
        ccv: "",
      },
    };
  },

  closePaymentModal() {
    this.isPaymentModalOpen = false;
  },

  // ================= MÁSCARAS DO CARTÃO =================

  formatCardNumber(event) {
    this.paymentForm.creditCard.number = VMasker.toPattern(
      event.target.value,
      "9999 9999 9999 9999",
    );
  },

  formatCardExpiry(event) {
    this.paymentForm.creditCard.expiry = VMasker.toPattern(
      event.target.value,
      "99/9999",
    );
  },

  formatCardCVV(event) {
    // Limita a 4 números
    let value = event.target.value.replace(/\D/g, "").substring(0, 4);
    this.paymentForm.creditCard.ccv = value;
  },

  // ================= SUBMIT PARA A API =================
  async submitPaymentChange() {
    this.isSubmittingPayment = true;

    try {
      let payload = {
        billing_type: this.paymentForm.billingType,
      };

      if (this.paymentForm.billingType === "CREDIT_CARD") {
        const [expiryMonth, expiryYear] =
          this.paymentForm.creditCard.expiry.split("/");

        payload.credit_card = {
          holder_name: this.paymentForm.creditCard.holderName,
          number: this.paymentForm.creditCard.number.replace(/\s/g, ""),
          expiry_month: expiryMonth,
          expiry_year: expiryYear,
          ccv: this.paymentForm.creditCard.ccv,
        };
      }

      // Direciona para a rota correta dependendo do modo do Modal
      if (this.modalMode === "reactivate") {
        await axios.post("/api/client/subscription/reactivate", payload);
        showAlert(
          "Assinatura reativada com sucesso! Bem-vindo de volta.",
          "success",
        );
      } else {
        await axios.put("/api/client/subscription/payment-method", payload);
        showAlert("Método de pagamento atualizado com sucesso!", "success");
      }

      this.closePaymentModal();
      await this.initSubscription(); // Atualiza a tela
    } catch (error) {
      handleRequestError(error, "Erro ao processar a solicitação financeira.");
    } finally {
      this.isSubmittingPayment = false;
    }
  },

  openCancelModal() {
    this.cancelReason = "";
    this.isCancelModalOpen = true;
  },

  closeCancelModal() {
    if (!this.isCanceling) {
      this.isCancelModalOpen = false;
    }
  },

  async submitCancelSubscription() {
    // Validate if a reason was selected (optional, but recommended for SaaS)
    if (!this.cancelReason) {
      showAlert(
        "Por favor, selecione um motivo para o cancelamento.",
        "warning",
      );
      return;
    }

    this.isCanceling = true;

    try {
      // Calls the Laravel API to handle the Asaas cancellation
      // Note: In Axios, DELETE requests require the payload to be passed within a 'data' object
      await axios.delete("/api/client/subscription/cancel", {
        data: {
          cancel_reason: this.cancelReason,
        },
      });

      showAlert("Sua assinatura foi cancelada com sucesso.", "success");

      this.isCancelModalOpen = false;

      // Refresh data to update UI status badges
      await this.initSubscription();
    } catch (error) {
      handleRequestError(error, "Erro ao tentar cancelar a assinatura.");
    } finally {
      this.isCanceling = false;
    }
  },

  // ==========================================
  // NOVO MÉTODO: REATIVAR ASSINATURA
  // ==========================================
  redirectToCheckout() {
    // Ativa o skeleton loading da tela para dar feedback visual de transição
    this.isLoading = true;
    window.location.href = "/client/checkout";
  },
});
